import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  MapPin,
  Users as UsersIcon,
  Trophy,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { SkelCard } from "@/components/Skeleton";
import "./events.css";

function formatWhenLong(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatWhenShort(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Events() {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api
      .get("/events")
      .then((r) => {
        const data = r.data;
        if (Array.isArray(data)) {
          setEvents({ upcoming: data, past: [] });
        } else {
          setEvents({
            upcoming: data?.upcoming || [],
            past: data?.past || [],
          });
        }
      })
      .catch(() => setEvents({ upcoming: [], past: [] }));
  }, []);

  const loading = events === null;
  const upcoming = events?.upcoming || [];
  const past = events?.past || [];
  const featured = upcoming[0];
  const restUpcoming = upcoming.slice(1);

  return (
    <div data-testid="events-page">
      <SEO
        title="Trading Events"
        description="Local Panini sticker-trading events and community meetups. Free RSVP, family-friendly, kids welcome. RSVP so we know you're coming."
      />

      {/* ── Compact hero masthead ───────────────────────────────── */}
      <section className="hero-black">
        <div className="events-wide events-hero" data-testid="events-hero">
          <div className="flex items-center gap-2">
            <span
              className="sticker sticker-white text-[11px] py-1 px-2"
              data-testid="events-hero-badge"
            >
              FREE RSVP
            </span>
          </div>
          <h1
            className="events-hero__title mt-2"
            data-testid="events-hero-title"
          >
            Local trading{" "}
            <span className="text-[var(--panini-yellow)]">events.</span>
          </h1>
          <p
            className="events-hero__subtitle mt-1.5"
            data-testid="events-hero-subtitle"
          >
            Come trade in person, meet other collectors, pick up orders, and
            bring the kids. Every event is free — just RSVP so we know you&apos;re
            coming.
          </p>
        </div>
        <div className="field-line" aria-hidden />
      </section>

      {/* ── Feature strip (3 up desktop) ───────────────────────── */}
      <section className="events-wide events-strip" data-testid="events-essentials">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          <StripCard
            Icon={UsersIcon}
            title="Family-friendly"
            body="Kids and parents always welcome."
          />
          <StripCard
            Icon={Trophy}
            title="Made for trading"
            body="Bring doubles and a want-list."
          />
          <StripCard
            Icon={Sparkles}
            title="Always free"
            body="No ticket, no cover — just RSVP."
          />
        </div>
      </section>

      {/* ── Upcoming events (featured + extras) ────────────────── */}
      <section
        className="events-wide events-section"
        data-testid="events-upcoming-section"
      >
        <div className="events-section__heading">
          <div>
            <div className="kicker kicker-red">On the schedule</div>
            <h2 className="events-section__title mt-1">Upcoming events.</h2>
          </div>
        </div>

        {loading && (
          <div className="grid grid-cols-1 gap-4">
            <SkelCard />
          </div>
        )}

        {!loading && !featured && (
          <div
            className="rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-6"
            data-testid="events-upcoming-empty"
          >
            <div className="kicker kicker-red">No upcoming events</div>
            <h3 className="font-display text-xl md:text-2xl mt-2">
              No upcoming events right now. Check back soon!
            </h3>
            <p className="text-[var(--smoke)] mt-2 text-sm max-w-md">
              We&apos;re lining up the next round of trading events.
            </p>
          </div>
        )}

        {!loading && featured && (
          <FeaturedEventCard event={featured} />
        )}

        {!loading && restUpcoming.length > 0 && (
          <div className="events-additional" data-testid="events-additional-upcoming">
            {restUpcoming.map((e) => (
              <CompactUpcomingCard key={e.id} event={e} />
            ))}
          </div>
        )}
      </section>

      {/* ── Past events (archive) ──────────────────────────────── */}
      {!loading && past.length > 0 && (
        <section
          className="events-wide events-section"
          data-testid="events-past-section"
        >
          <div className="events-section__heading">
            <div>
              <div className="kicker kicker-black">In the archive</div>
              <h2 className="events-section__title mt-1">Past events.</h2>
            </div>
          </div>
          <div className="events-past-grid" data-testid="events-list-past">
            {past.map((e) => (
              <PastEventCard key={e.id} event={e} />
            ))}
          </div>
        </section>
      )}

      {/* small bottom rhythm */}
      <div className="pb-10 lg:pb-14" aria-hidden />
    </div>
  );
}

/* ─────────── sub-components ─────────── */

function StripCard({ Icon, title, body }) {
  return (
    <div className="events-strip__card" data-testid={`events-strip-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="events-strip__icon">
        <Icon size={18} />
      </div>
      <div>
        <div className="events-strip__title">{title}</div>
        <p className="events-strip__body">{body}</p>
      </div>
    </div>
  );
}

function FeaturedEventCard({ event: e }) {
  const flyer = e.primary_flyer_url || e.cover_image_url || "";
  return (
    <Link
      to={`/events/${e.slug || e.id}`}
      className="events-featured"
      data-testid={`event-card-featured-${e.id}`}
    >
      <div className="events-featured__media">
        <span className="sticker sticker-red events-featured__badge text-[11px] py-1 px-2">
          FREE
        </span>
        {flyer ? (
          <img
            src={flyer}
            alt={e.title}
            className="events-featured__img"
            loading="eager"
            data-testid="events-featured-flyer"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/60 text-xs">
            No flyer yet
          </div>
        )}
      </div>
      <div className="events-featured__body">
        <div>
          <div className="kicker kicker-red">Next event</div>
          <h3 className="events-featured__title" data-testid="events-featured-title">
            {e.title}
          </h3>
          <div className="events-featured__meta mt-2">
            <span>
              <Calendar size={14} /> {formatWhenLong(e.starts_at)}
            </span>
            <span>
              <MapPin size={14} /> {e.location}
            </span>
          </div>
          <p className="events-featured__desc mt-2">{e.description}</p>
        </div>
        <div className="pt-1">
          <span className="events-featured__cta" data-testid="events-featured-cta">
            RSVP now <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function CompactUpcomingCard({ event: e }) {
  const flyer = e.primary_flyer_url || e.cover_image_url || "";
  return (
    <Link
      to={`/events/${e.slug || e.id}`}
      className="events-past-card"
      data-testid={`event-card-upcoming-${e.id}`}
      data-section="upcoming"
    >
      <div className="events-past-card__media">
        <span className="sticker sticker-red events-past-card__badge text-[10px] py-0.5 px-1.5">
          FREE
        </span>
        {flyer && (
          <img src={flyer} alt={e.title} className="events-past-card__img" loading="lazy" />
        )}
      </div>
      <div className="events-past-card__body">
        <h4 className="events-past-card__title">{e.title}</h4>
        <div className="events-past-card__meta">
          <span>
            <Calendar size={12} /> {formatWhenShort(e.starts_at)}
          </span>
          <span>
            <MapPin size={12} /> {e.location}
          </span>
        </div>
        <p className="events-past-card__desc">{e.description}</p>
        <span className="events-past-card__cta">
          RSVP now <ArrowRight size={12} />
        </span>
      </div>
    </Link>
  );
}

function PastEventCard({ event: e }) {
  const flyer = e.primary_flyer_url || e.cover_image_url || "";
  return (
    <Link
      to={`/events/${e.slug || e.id}`}
      className="events-past-card"
      data-testid={`event-card-past-${e.id}`}
      data-section="past"
    >
      <div className="events-past-card__media">
        <span className="sticker sticker-white events-past-card__badge text-[10px] py-0.5 px-1.5">
          PAST
        </span>
        {flyer && (
          <img
            src={flyer}
            alt={e.title}
            className="events-past-card__img"
            loading="lazy"
          />
        )}
      </div>
      <div className="events-past-card__body">
        <h4 className="events-past-card__title">{e.title}</h4>
        <div className="events-past-card__meta">
          <span>
            <Calendar size={12} /> {formatWhenShort(e.starts_at)}
          </span>
          <span>
            <MapPin size={12} /> {e.location}
          </span>
        </div>
        <p className="events-past-card__desc">{e.description}</p>
        <span className="events-past-card__cta">
          View event <ArrowRight size={12} />
        </span>
      </div>
    </Link>
  );
}
